import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InviteApplicantState,
  InviteProfilesFilter,
} from '../types/inviteApplicant';
import {
  fetchInviteProfileDetails,
  fetchInviteProfileList,
  rejectJobProfile,
  sendJobInvite,
  downloadDBResume,
  getInvitedUserProfile,
  fetchDbAccessStatus,
  unlockDbAccess,
} from '../actions/inviteApplicant';
import { getPlanById } from '../actions/premium';
import { fetchJobByIdForEmployer } from '../actions/employerLayout';

// profile_analysis_complete socket payload — keyed on user_id, distinct from the
// applicant-review AnalyseSocketResponse (which keys on the JobApplicants row id).
export type ProfileAnalysisCompletePayload = {
  profile_user_id?: string;
  job_description_id?: string;
  status?: string;
  overall_score?: number;
};

const initialState: InviteApplicantState = {
  inviteProfiles: null,
  jobData: null,
  selectedProfileDetails: null,
  fetchingInviteProfiles: false,
  fetchingProfileDetails: false,
  selectedProfileIndex: -1,
  isDownloadingResume: false,
  filters: {
    userStatus: 'all',
    primaryFilter: 'aiMatch',
  },
  jobPlanDetails: null,
  dbAccess: {
    isUnlocked: false,
    eligible: false,
    matchingProfileCount: 0,
    cost: 250,
    loading: false,
  },
  bulkAnalysis: {
    batchId: null,
    total: 0,
    done: 0,
    running: false,
  },
};

/* eslint-disable no-param-reassign */
const inviteApplicantSlice = createSlice({
  name: 'inviteApplicant',
  initialState,
  reducers: {
    setSelectedProfileIndex: (state, action: PayloadAction<number>) => {
      state.selectedProfileIndex = action.payload;
    },
    setFilters: (state, action: PayloadAction<InviteProfilesFilter>) => {
      state.filters = action.payload;
      state.inviteProfiles = null;
      state.selectedProfileIndex = -1;
    },
    onProfileAnalysisComplete: (
      state,
      action: PayloadAction<ProfileAnalysisCompletePayload>,
    ) => {
      const {
        profile_user_id: profileUserId,
        status,
        overall_score: overallScore,
      } = action.payload;
      if (!profileUserId || !state.inviteProfiles?.job_profiles) {
        return;
      }
      // Fill the matching DB card in place — match on user_id (the socket key),
      // not the match-row id. No re-sort: the list order is preserved.
      state.inviteProfiles.job_profiles = state.inviteProfiles.job_profiles.map(
        (profile) =>
          profile.user_id === profileUserId
            ? {
                ...profile,
                resume_analysis_status:
                  status ?? profile.resume_analysis_status,
                resume_analysis_overall_score:
                  overallScore ?? profile.resume_analysis_overall_score,
              }
            : profile,
      );
    },
    setBulkProgress: (
      state,
      action: PayloadAction<{ batch_id: string; done: number; total: number }>,
    ) => {
      // Ignore progress from a different (stale) batch.
      if (
        state.bulkAnalysis.batchId &&
        state.bulkAnalysis.batchId !== action.payload.batch_id
      ) {
        return;
      }
      state.bulkAnalysis.batchId = action.payload.batch_id;
      state.bulkAnalysis.done = action.payload.done;
      state.bulkAnalysis.total = action.payload.total;
      state.bulkAnalysis.running = action.payload.done < action.payload.total;
    },
    resetBulkProgress: (state) => {
      state.bulkAnalysis = { ...initialState.bulkAnalysis };
    },
    resetState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchInviteProfileList.pending, (state) => {
        state.fetchingInviteProfiles = true;
      })
      .addCase(fetchInviteProfileList.fulfilled, (state, action) => {
        if (action.payload.current_page === 1 || !state.inviteProfiles) {
          state.inviteProfiles = action.payload;
        } else {
          const updatedList = [
            ...(state.inviteProfiles.job_profiles ?? []),
            ...(action.payload.job_profiles ?? []),
          ];
          state.inviteProfiles = {
            ...action.payload,
            job_profiles: updatedList,
          };
        }

        const profileIdx =
          action.payload.job_profiles?.findIndex(
            (profile) => profile.id === action.meta.arg.selectedProfileId,
          ) ?? -1;
        if (
          state.selectedProfileIndex < 0 &&
          action.meta.arg.selectedProfileId &&
          profileIdx >= 0
        ) {
          state.selectedProfileIndex =
            (action.payload.page_size ?? 10) *
              ((action.payload.current_page ?? 1) - 1) +
            profileIdx;
        }

        state.fetchingInviteProfiles = false;
      })
      .addCase(fetchInviteProfileList.rejected, (state) => {
        state.fetchingInviteProfiles = false;
      });
    builder.addCase(fetchJobByIdForEmployer.fulfilled, (state, action) => {
      state.jobData = action.payload;
    });
    builder
      .addCase(fetchInviteProfileDetails.pending, (state) => {
        state.fetchingProfileDetails = true;
      })
      .addCase(fetchInviteProfileDetails.fulfilled, (state, action) => {
        state.selectedProfileDetails = action.payload;
        state.fetchingProfileDetails = false;
      })
      .addCase(fetchInviteProfileDetails.rejected, (state) => {
        state.fetchingProfileDetails = false;
      });

    builder.addCase(sendJobInvite.fulfilled, (state, action) => {
      if (
        state.selectedProfileDetails?.profile_match_details?.id ===
        action.meta.arg.profileId
      ) {
        state.selectedProfileDetails.profile_match_details = {
          ...state.selectedProfileDetails.profile_match_details,
          is_invited: true,
        };
      }
      if (state.inviteProfiles?.job_profiles) {
        state.inviteProfiles.job_profiles =
          state.inviteProfiles.job_profiles.map((profile) =>
            profile.id === action.meta.arg.profileId
              ? { ...profile, is_invited: true }
              : profile,
          );
      }

      if (state.jobData?.job) {
        state.jobData.job.total_invited =
          (state.jobData.job.total_invited ?? 0) + 1;
      }
    });
    builder.addCase(rejectJobProfile.fulfilled, (state, action) => {
      if (
        state.selectedProfileDetails?.profile_match_details?.id ===
        action.meta.arg.profileId
      ) {
        state.selectedProfileDetails.profile_match_details.is_manually_rejected = true;
      }

      if (state.inviteProfiles?.job_profiles) {
        state.inviteProfiles.job_profiles =
          state.inviteProfiles.job_profiles.map((profile) => {
            if (profile.id === action.meta.arg.profileId) {
              return { ...profile, is_manually_rejected: true };
            }
            return profile;
          });
      }
    });
    builder
      .addCase(downloadDBResume.pending, (state) => {
        state.isDownloadingResume = true;
      })
      .addCase(downloadDBResume.fulfilled, (state, action) => {
        const { profileId } = action.meta.arg;
        let isFirstTimeDownload = false;
        if (
          state.selectedProfileDetails?.profile_match_details?.id ===
            profileId &&
          !state.selectedProfileDetails.profile_match_details
            .is_db_resume_download
        ) {
          state.selectedProfileDetails.profile_match_details.is_db_resume_download = true;
          isFirstTimeDownload = true;
        }

        if (state.inviteProfiles && isFirstTimeDownload) {
          state.inviteProfiles.job_profiles =
            state.inviteProfiles.job_profiles?.map((profile) => {
              if (profile.id === profileId && !profile.is_db_resume_download) {
                isFirstTimeDownload = true;
                return { ...profile, is_db_resume_download: true };
              }
              return profile;
            });

          state.inviteProfiles.db_resume_download_count =
            (state.inviteProfiles.db_resume_download_count ?? 0) + 1;
        }
        state.isDownloadingResume = false;
      })
      .addCase(downloadDBResume.rejected, (state) => {
        state.isDownloadingResume = false;
      });

    builder
      .addCase(getInvitedUserProfile.pending, (state) => {
        state.isDownloadingResume = true;
      })
      .addCase(getInvitedUserProfile.fulfilled, (state, action) => {
        const { profileId } = action.meta.arg;
        let isFirstTimeDownload = false;
        if (
          state.selectedProfileDetails?.profile_match_details?.id ===
            profileId &&
          !state.selectedProfileDetails.profile_match_details
            .is_db_resume_download
        ) {
          state.selectedProfileDetails.profile_match_details.is_db_resume_download = true;
          isFirstTimeDownload = true;
        }

        if (state.inviteProfiles && isFirstTimeDownload) {
          state.inviteProfiles.job_profiles =
            state.inviteProfiles.job_profiles?.map((profile) => {
              if (profile.id === profileId && !profile.is_db_resume_download) {
                isFirstTimeDownload = true;
                return { ...profile, is_db_resume_download: true };
              }
              return profile;
            });

          state.inviteProfiles.db_resume_download_count =
            (state.inviteProfiles.db_resume_download_count ?? 0) + 1;
        }

        state.selectedProfileDetails = {
          ...action.payload,
        };
        state.isDownloadingResume = false;
      })
      .addCase(getInvitedUserProfile.rejected, (state) => {
        state.isDownloadingResume = false;
      });
    builder.addCase(getPlanById.fulfilled, (state, action) => {
      state.jobPlanDetails = action.payload;
    });

    builder
      .addCase(fetchDbAccessStatus.pending, (state) => {
        state.dbAccess.loading = true;
      })
      .addCase(fetchDbAccessStatus.fulfilled, (state, action) => {
        state.dbAccess = {
          isUnlocked: action.payload.is_unlocked ?? false,
          eligible: action.payload.eligible ?? false,
          matchingProfileCount: action.payload.matching_profile_count ?? 0,
          cost: action.payload.cost ?? 250,
          loading: false,
        };
      })
      .addCase(fetchDbAccessStatus.rejected, (state, action) => {
        if (!action.meta.aborted) {
          state.dbAccess.loading = false;
        }
      });

    builder.addCase(unlockDbAccess.fulfilled, (state, action) => {
      state.dbAccess.isUnlocked = true;
      // Seed the analysis progress bar from the unlock response so the UI shows
      // it immediately, before the first bulk_analysis_progress socket lands.
      const total = action.payload.count ?? 0;
      state.bulkAnalysis = {
        batchId: action.payload.batch_id ?? null,
        total,
        done: 0,
        running: total > 0,
      };
    });
  },
});

export const {
  setSelectedProfileIndex,
  setFilters,
  onProfileAnalysisComplete,
  setBulkProgress,
  resetBulkProgress,
  resetState,
} = inviteApplicantSlice.actions;

export default inviteApplicantSlice.reducer;
